/**
 * The contract type's auto-approval switch — CLM-121 (D-110-4 points 3 and 6;
 * D-110-3 point 3; FR-32, NFR-16, CR-08, FR-34). The refinements R-121-1 …
 * R-121-21 are the spec's (`phase4-specs/CLM-121-type-auto-approval.md` §3.3).
 *
 * Two endpoints, rendered as the "Contract type" card on the existing
 * Auto-approval page (R-121-16). CLM-108's `verification-policy` response is not
 * widened: the type has no field row and is never listed there.
 *
 * | Name | Gate | What it does |
 * |---|---|---|
 * | `contract-type-auto-approval` | `admin:configure`; the figures need Scope-All | the switch, its state and its figures |
 * | `set-contract-type-auto-approval` | `admin:configure` + Scope-All | switch it on or off |
 *
 * ## Off by default, an Admin's alone, with fixed figures
 *
 * The switch is the type's `clm.verification_policy` row (R-121-2): `threshold`
 * is on, bound to the grain it was taken at; `always_verify` is off; **no row is
 * off**. A Scope-All Admin switches it alone — no Legal co-sign (Gerry,
 * 1943297) — and switching on is refused until the counted figure is strictly
 * above 0.900 over at least 30 decisions at the current grain. The four numbers
 * are fixed ({@link CONTRACT_TYPE_AUTO_APPROVAL_FIGURES}), never settings.
 *
 * ## What counts (D-110-4 point 6 (b))
 *
 * `basis` counts only an Admin's counted decisions on contracts whose file name
 * gave no type: `no_name_type`, and `name_evidence_unknown` on a person's type,
 * the AI's or a placeholder `OTHER` — never a legacy or file-name MSA, NDA or
 * SOW with no evidence recorded. `against_file_name` —
 * "AI against the file name", the `disagree` decisions — is shown beside it and
 * never read by a rule.
 *
 * ## Scope (R-121-16)
 *
 * The switch's configuration (each revision's on/off, grain, who, when and
 * note) is shown to every Admin. Everything computed on the org-wide figure —
 * the state, the figures, the pause (which names a contract and an Admin), the
 * lapse, the activity and each revision's recorded basis — is shown to a
 * Scope-All caller only; a practice-scoped Admin gets `withheld:
 * 'scope_all_required'` and `null`s, CLM-108's `fields: null` rule (R-108-12).
 *
 * ## Exact values on the wire
 *
 * Rates are `numeric(4,3)` decimal strings, display only; the state compares
 * the counts (R-107-8). Every count is exact.
 */
import { z } from 'zod';
import { AuditReceiptSchema } from '../provenance.js';
import { ContractKeySchema, IsoTimestampUtcSchema, UpnSchema } from '../primitives.js';
import { CONTRACT_TYPE_AUTO_APPROVAL_FIGURES, ContractTypeAutoApprovalPauseKindSchema, ContractTypeAutoApprovalStateSchema, ThresholdSchema, } from '../vocabulary.js';
import { VerificationPolicyNoteSchema } from './verification-policy.js';
/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                  */
/* -------------------------------------------------------------------------- */
/** Why the figures are absent: the caller is not Scope-All (R-121-16). */
export const CONTRACT_TYPE_AUTO_APPROVAL_WITHHELD = ['scope_all_required'];
/** The two acts. */
export const CONTRACT_TYPE_AUTO_APPROVAL_SWITCHES = ['on', 'off'];
/**
 * Why switching on is not offered now (R-121-13), in the order the route
 * tests them: no pipeline reading of the type to take a grain from; fewer than
 * 30 counted decisions at that grain; not strictly above 0.900; already on at
 * this grain, neither paused nor lapsed.
 */
export const CONTRACT_TYPE_SWITCH_ON_REFUSALS = ['no_reading_grain', 'too_few_decisions', 'not_above_threshold', 'no_change'];
export const ContractTypeSwitchOnRefusalSchema = z.enum(CONTRACT_TYPE_SWITCH_ON_REFUSALS);
/**
 * Every coded refusal of `set-contract-type-auto-approval`, each a
 * `field_errors` entry `code: sentence` (CLM-109's shape): 400 `note_required`
 * and `no_change`; 409 `policy_changed`, `no_reading_grain`,
 * `too_few_decisions` and `not_above_threshold`.
 */
export const CONTRACT_TYPE_AUTO_APPROVAL_REFUSALS = [
    'note_required',
    'policy_changed',
    'no_change',
    'no_reading_grain',
    'too_few_decisions',
    'not_above_threshold',
];
/* -------------------------------------------------------------------------- */
/* Shared shapes                                                               */
/* -------------------------------------------------------------------------- */
const Count = z.number().int().min(0);
const Revision = z.number().int().min(1);
/** A grain: the schema version and the model of a reading (D-2.1-3). No definition revision: the type has none. */
export const ContractTypeGrainSchema = z
    .object({
    extraction_schema_version: z.string().min(1).max(20),
    extraction_model: z.string().min(1).max(120),
})
    .strict();
/**
 * A figure at a grain — `fn_contract_type_policy_basis` (6b) or
 * `fn_contract_type_against_name_basis`. `sample_size` is Verified + Corrected;
 * the rate is display only.
 */
export const ContractTypeAutoApprovalBasisSchema = z
    .object({
    verified_count: Count,
    corrected_count: Count,
    sample_size: Count,
    accuracy_rate: ThresholdSchema.nullable(),
    last_decision_utc: IsoTimestampUtcSchema.nullable(),
})
    .strict()
    .superRefine((basis, ctx) => {
    if (basis.sample_size !== basis.verified_count + basis.corrected_count) {
        ctx.addIssue({ code: 'custom', path: ['sample_size'], message: 'sample_size is verified_count + corrected_count' });
    }
    if ((basis.accuracy_rate === null) !== (basis.sample_size === 0)) {
        ctx.addIssue({ code: 'custom', path: ['accuracy_rate'], message: 'accuracy_rate is null exactly at sample_size 0 — never 0' });
    }
    if ((basis.last_decision_utc === null) !== (basis.sample_size === 0)) {
        ctx.addIssue({ code: 'custom', path: ['last_decision_utc'], message: 'the sample span is null exactly at sample_size 0' });
    }
});
/** Strictly above 0.900, compared in integers (R-121-3): `verified × 1000 > 900 × n`. */
export function typeFigureQualifies(basis) {
    return (basis.sample_size >= CONTRACT_TYPE_AUTO_APPROVAL_FIGURES.minimum_sample &&
        basis.verified_count * 1000 > CONTRACT_TYPE_AUTO_APPROVAL_FIGURES.threshold_milli * basis.sample_size);
}
/**
 * One revision of the type's switch — a `clm.verification_policy` row of
 * `classification.contract_type`. `on` is `threshold`, `off` is `always_verify`.
 * `basis` is the org-wide count the revision was taken on (FR-31's audit row
 * carries the same), `null` exactly when the caller is not Scope-All.
 */
export const TypeSwitchRevisionSchema = z
    .object({
    policy_revision: Revision,
    on: z.boolean(),
    /** The grain the revision was taken at; `null` for an `off` taken with no reading. */
    grain: ContractTypeGrainSchema.nullable(),
    basis: ContractTypeAutoApprovalBasisSchema.nullable(),
    changed_by_upn: UpnSchema,
    changed_utc: IsoTimestampUtcSchema,
    change_note: VerificationPolicyNoteSchema.nullable(),
})
    .strict()
    .superRefine((revision, ctx) => {
    if (revision.on && (revision.grain === null || revision.change_note === null)) {
        ctx.addIssue({ code: 'custom', path: ['on'], message: 'switching on records a grain and a note (ck_verification_policy_contract_type)' });
    }
    if (revision.on && revision.basis !== null && !typeFigureQualifies(revision.basis)) {
        ctx.addIssue({ code: 'custom', path: ['basis'], message: 'switching on rests on at least 30 counted decisions, strictly above 0.900' });
    }
    if (revision.grain === null && revision.basis !== null && revision.basis.sample_size !== 0) {
        ctx.addIssue({ code: 'custom', path: ['basis'], message: 'with no grain there is no basis' });
    }
});
/** The fixed figures, as the server applied them — literals, so a drifted constant is a 500, never a silent change. */
export const ContractTypeAutoApprovalFiguresSchema = z
    .object({
    threshold: z.literal(CONTRACT_TYPE_AUTO_APPROVAL_FIGURES.threshold),
    threshold_milli: z.literal(CONTRACT_TYPE_AUTO_APPROVAL_FIGURES.threshold_milli),
    minimum_sample: z.literal(CONTRACT_TYPE_AUTO_APPROVAL_FIGURES.minimum_sample),
    sample_rate: z.literal(CONTRACT_TYPE_AUTO_APPROVAL_FIGURES.sample_rate),
    sample_cutoff: z.literal(CONTRACT_TYPE_AUTO_APPROVAL_FIGURES.sample_cutoff),
    stale_after_days: z.literal(CONTRACT_TYPE_AUTO_APPROVAL_FIGURES.stale_after_days),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* contract-type-auto-approval — the card                                      */
/* -------------------------------------------------------------------------- */
/** No parameters. `.strict()`, so a `scope` or `threshold` key is a 400, never ignored. */
export const ContractTypeAutoApprovalRequestSchema = z.object({}).strict();
/** The correction that paused the switch (R-121-9). Names a contract and an Admin, so Scope-All only. */
export const ContractTypeAutoApprovalPauseSchema = z
    .object({
    decision_audit_id: z.string().regex(/^\d{1,19}$/),
    contract_key: ContractKeySchema,
    decided_by_upn: UpnSchema.nullable(),
    decided_utc: IsoTimestampUtcSchema,
    kind: ContractTypeAutoApprovalPauseKindSchema,
})
    .strict();
/** The first reading at another grain since the switch-on: the lapse, which stays until the Admin switches on again (R-121-8). */
export const ContractTypeAutoApprovalLapseSchema = z
    .object({
    since_utc: IsoTimestampUtcSchema,
    extraction_schema_version: z.string().min(1).max(20),
    extraction_model: z.string().min(1).max(120),
})
    .strict();
export const ContractTypeSwitchOnSchema = z
    .object({ allowed: z.boolean(), refusal: ContractTypeSwitchOnRefusalSchema.nullable() })
    .strict()
    .refine((offer) => offer.allowed === (offer.refusal === null), {
    message: 'switching on is allowed exactly when nothing refuses it',
    path: ['refusal'],
});
/** What the type step did, from its act rows in scope (R-121-11). */
export const ContractTypeAutoApprovalActivitySchema = z
    .object({
    /** AI-decided types in the last 30 days: approved by policy, sampled, and sent to an Admin for any other reason. */
    last_30_days: z.object({ approved: Count, sampled: Count, sent_to_admin: Count }).strict(),
    /** Agreements now approved by policy (`auto_confirmed`, `ai_proposed`). */
    policy_approved_total: Count,
    /** Provisional AI-decided agreements whose latest reading is a safety sample — waiting in Type proposals. */
    open_samples: Count,
})
    .strict();
export const ContractTypeAutoApprovalResponseSchema = z
    .object({
    generated_utc: IsoTimestampUtcSchema,
    figures: ContractTypeAutoApprovalFiguresSchema,
    switch: z
        .object({
        /** The latest revision, or `null`: no row is off. */
        current: TypeSwitchRevisionSchema.nullable(),
        /** Every revision, newest first; includes `current`. */
        history: z.array(TypeSwitchRevisionSchema),
    })
        .strict(),
    withheld: z.enum(CONTRACT_TYPE_AUTO_APPROVAL_WITHHELD).nullable(),
    state: ContractTypeAutoApprovalStateSchema.nullable(),
    /** The grain of the latest pipeline reading of the type — the one the page and a switch-on are evaluated at. */
    grain: ContractTypeGrainSchema.nullable(),
    /** The auto-approval figure (6b) at `grain` — zeros when there is none. */
    basis: ContractTypeAutoApprovalBasisSchema.nullable(),
    /** "AI against the file name" at `grain` — display only, never read by a rule. */
    against_file_name: ContractTypeAutoApprovalBasisSchema.nullable(),
    /** `last_decision_utc + 90 days`, exactly when the state is `stale`. */
    stale_since_utc: IsoTimestampUtcSchema.nullable(),
    pause: ContractTypeAutoApprovalPauseSchema.nullable(),
    lapse: ContractTypeAutoApprovalLapseSchema.nullable(),
    switch_on: ContractTypeSwitchOnSchema.nullable(),
    activity: ContractTypeAutoApprovalActivitySchema.nullable(),
})
    .strict()
    .superRefine((body, ctx) => {
    const { current, history } = body.switch;
    if ((current === null) !== (history.length === 0) || (current !== null && history[0]?.policy_revision !== current.policy_revision)) {
        ctx.addIssue({ code: 'custom', path: ['switch', 'current'], message: 'current is the newest revision in history, or null with none' });
    }
    history.forEach((revision, index) => {
        const newer = history[index - 1];
        if (newer !== undefined && newer.policy_revision !== revision.policy_revision + 1) {
            ctx.addIssue({ code: 'custom', path: ['switch', 'history', index], message: 'history is every revision, newest first, gapless' });
        }
    });
    const withheld = body.withheld !== null;
    const figureKeys = ['state', 'grain', 'basis', 'against_file_name', 'stale_since_utc', 'pause', 'lapse', 'switch_on', 'activity'];
    if (withheld) {
        for (const key of figureKeys) {
            if (body[key] !== null)
                ctx.addIssue({ code: 'custom', path: [key], message: 'a figure is withheld from a caller who is not Scope-All' });
        }
        history.forEach((revision, index) => {
            if (revision.basis !== null) {
                ctx.addIssue({ code: 'custom', path: ['switch', 'history', index, 'basis'], message: 'a revision’s org-wide basis is withheld too (R-121-16)' });
            }
        });
        return;
    }
    for (const key of ['state', 'basis', 'against_file_name', 'switch_on', 'activity']) {
        if (body[key] === null)
            ctx.addIssue({ code: 'custom', path: [key], message: 'a Scope-All caller gets every figure' });
    }
    history.forEach((revision, index) => {
        if (revision.basis === null) {
            ctx.addIssue({ code: 'custom', path: ['switch', 'history', index, 'basis'], message: 'a Scope-All caller sees each revision’s basis' });
        }
    });
    const on = current?.on ?? false;
    const state = body.state;
    if ((state === 'switched_off') !== !on) {
        ctx.addIssue({ code: 'custom', path: ['state'], message: 'switched_off is exactly the state with no switch-on in force' });
    }
    if (state === 'not_read' && body.grain !== null) {
        ctx.addIssue({ code: 'custom', path: ['state'], message: 'not_read means no pipeline reading of the type' });
    }
    if (body.grain === null && body.basis !== null && body.basis.sample_size + (body.against_file_name?.sample_size ?? 0) !== 0) {
        ctx.addIssue({ code: 'custom', path: ['basis'], message: 'with no grain there is no figure' });
    }
    if (state === 'lapsed' && body.lapse === null) {
        ctx.addIssue({ code: 'custom', path: ['lapse'], message: 'a lapsed switch names the reading that lapsed it' });
    }
    if (state === 'paused_by_correction' && body.pause === null) {
        ctx.addIssue({ code: 'custom', path: ['pause'], message: 'a paused switch names the correction that paused it' });
    }
    if ((body.pause !== null || body.lapse !== null) && !on) {
        ctx.addIssue({ code: 'custom', path: ['pause'], message: 'only a switch that is on pauses or lapses' });
    }
    if ((body.stale_since_utc !== null) !== (state === 'stale')) {
        ctx.addIssue({ code: 'custom', path: ['stale_since_utc'], message: 'stale_since_utc is set exactly when the state is stale' });
    }
    if (state === 'too_few_decisions' && body.basis !== null && body.basis.sample_size >= body.figures.minimum_sample) {
        ctx.addIssue({ code: 'custom', path: ['state'], message: 'too_few_decisions is below 30 counted decisions' });
    }
    if (state === 'on' && body.basis !== null && !typeFigureQualifies(body.basis)) {
        ctx.addIssue({ code: 'custom', path: ['state'], message: 'on needs 30 counted decisions, strictly above 0.900' });
    }
});
/* -------------------------------------------------------------------------- */
/* set-contract-type-auto-approval                                             */
/* -------------------------------------------------------------------------- */
/**
 * Switch it on or off. No counts, no basis, no UPN: the server captures the
 * figure and binds the Admin from the session. `on` needs a note, refused as
 * the coded `note_required`.
 */
export const SetContractTypeAutoApprovalRequestSchema = z
    .object({
    switch: z.enum(CONTRACT_TYPE_AUTO_APPROVAL_SWITCHES),
    /** The revision the caller saw; `0` = no row yet. A stale page is 409 policy_changed, never an overwrite. */
    expected_policy_revision: z.number().int().min(0),
    change_note: VerificationPolicyNoteSchema.optional(),
})
    .strict()
    .refine((body) => body.switch !== 'on' || body.change_note !== undefined, {
    message: 'note_required: switching auto-approval on needs its reason recorded (R-121-13)',
    path: ['change_note'],
});
/** The revision written, the state now, and the `0046` Create receipt (FR-31's audit row). */
export const SetContractTypeAutoApprovalResponseSchema = z
    .object({
    revision: TypeSwitchRevisionSchema,
    state: ContractTypeAutoApprovalStateSchema,
    audit: AuditReceiptSchema,
})
    .strict()
    .refine((body) => body.revision.basis !== null, {
    message: 'the writer is Scope-All, so the revision carries its basis',
    path: ['revision', 'basis'],
})
    .refine((body) => (body.state === 'switched_off') === !body.revision.on, {
    message: 'an off revision leaves the switch off; an on revision leaves it on',
    path: ['state'],
});
//# sourceMappingURL=contract-type-auto-approval.js.map